/**
 * Libraries
 */
import React, { useEffect } from 'react'
import className from 'classnames/bind'

/**
 * Assets
 */
import { Text } from 'app/components/common/'
import stylesModule from 'app/components/spinner/robot-spinner.module.scss'
import { translate } from 'app/services/localization/localization.service'

const cx = className.bind(stylesModule)

type RobotSpinnerProps = {
  text?: string
  classNames?: string
}

const updateSrOnlyStatus = (text: string) => {
  const srOnlyStatusEl = document.getElementById('sr-only-status')
  if (srOnlyStatusEl) {
    srOnlyStatusEl.textContent = text
  }
}

/**
 * Custom spinner for
 * HUB application
 */
const RobotSpinner = ({
  text = 'MESSAGE.LOADING',
  classNames,
}: RobotSpinnerProps) => {
  const visibleText = translate(text)
  const accessibleText = visibleText.replace(/\.\.\./g, '')

  useEffect(() => {
    updateSrOnlyStatus(accessibleText)

    return () => updateSrOnlyStatus('')
  }, [accessibleText])

  return (
    <div
      className={cx('spinner', classNames)}
      data-testid="robot-spinner"
      aria-hidden="true"
    >
      <svg
        className={cx('robot')}
        version="1.1"
        xmlns="http:/www.w3.org/2000/svg"
        x="0px"
        y="0px"
        width="165px"
        height="162.8px"
        viewBox="0 0 147.9 186"
      >
        <defs />
        <g>
          <g className="neck">
            <rect
              x="68.5"
              y="58.8"
              className={cx('st0')}
              width="10.3"
              height="39.1"
            />
            <g>
              <line
                className={cx('st1')}
                x1="78.7"
                y1="89.7"
                x2="68.5"
                y2="89.6"
              />
              <line
                className={cx('st1')}
                x1="78.6"
                y1="97.4"
                x2="68.4"
                y2="97.3"
              />
              <line
                className={cx('st1')}
                x1="78.9"
                y1="58.2"
                x2="68.8"
                y2="58.1"
              />
              <line
                className={cx('st1')}
                x1="78.7"
                y1="81.8"
                x2="68.6"
                y2="81.7"
              />
              <line
                className={cx('st1')}
                x1="78.8"
                y1="73.9"
                x2="68.6"
                y2="73.8"
              />
              <line
                className={cx('st1')}
                x1="78.6"
                y1="66"
                x2="68.5"
                y2="65.9"
              />
            </g>
          </g>
          <radialGradient
            id="SVGID_1b_"
            cx="47.7923"
            cy="-385.112"
            r="47.7923"
            gradientTransform="matrix(1 0 0 0.3836 0 166.0828)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#000" stopOpacity="0.2" />
            <stop offset="1" stopColor="#000" stopOpacity="0" />
          </radialGradient>
          <ellipse
            className="shadow"
            cx="47.8"
            cy="18.3"
            rx="47.8"
            ry="18.3"
            fill="url(#SVGID_1b_)"
            transform="translate (25 140)"
          />
          <g className="body">
            <circle className={cx('st2')} cx="73" cy="151.7" r="11.1" />
            <circle className={cx('st0')} cx="73" cy="151.4" r="7.5" />
            <polygon
              className={cx('st3')}
              points="82.6,63.1 65.2,63.1 63.3,58.7 84.6,58.7    "
            />
            <polygon
              className={cx('st3')}
              points="84.6,97.9 63.3,97.9 65.3,93.5 82.7,93.5    "
            />
            <rect
              x="42"
              y="96.7"
              className={cx('st3')}
              width="64"
              height="54.9"
            />
            <polygon
              className={cx('st3')}
              points="42,132.6 37.3,134.6 37.3,112.6 42,114.6"
            />
            <polygon
              className={cx('st4')}
              points="105.7,114.6 110.4,112.6 110.4,134.6 105.7,132.6"
              fill="#c7cdd1;"
            />
          </g>
          <g className={cx('hand-animation_r')}>
            <circle className={cx('st5')} cx="6.3" cy="85.9" r="3.1" />
            <path
              className={cx('st6')}
              d="M11.6,70v6.7c0,2.9-2.3,5.2-5.2,5.2h0c-2.9,0-5.2-2.3-5.2-5.2V70"
            />
          </g>
          <polygon
            className={cx('st3')}
            points="105.9,132.6 110.6,134.6 110.6,112.6 105.9,114.6    "
          />
          <g className="arm_l">
            <path
              className={cx('st0')}
              d="M117.2,128.6h-6.5v-10.4h6.5c10.5,0,19-8.5,19-19V89h10.4v10.2C146.6,115.4,133.4,128.6,117.2,128.6z"
            />
            <g>
              <line
                className={cx('st1')}
                x1="135.8"
                y1="103.3"
                x2="145.7"
                y2="105.5"
              />
              <line
                className={cx('st1')}
                x1="136.2"
                y1="96.8"
                x2="146.4"
                y2="96.8"
              />
              <line
                className={cx('st1')}
                x1="118.2"
                y1="118.1"
                x2="118.8"
                y2="128.3"
              />
              <line
                className={cx('st1')}
                x1="133.4"
                y1="109"
                x2="142.2"
                y2="114.3"
              />
              <line
                className={cx('st1')}
                x1="129.4"
                y1="113.7"
                x2="136"
                y2="121.5"
              />
              <line
                className={cx('st1')}
                x1="124.2"
                y1="117.1"
                x2="128"
                y2="126.5"
              />
            </g>
          </g>
          <g className={cx('hand-animation_l')}>
            <circle className={cx('st5')} cx="141.6" cy="86" r="3.1" />
            <path
              className={cx('st6')}
              d="M136.3,70v6.7c0,2.9,2.3,5.2,5.2,5.2h0c2.9,0,5.2-2.3,5.2-5.2V70"
            />
          </g>
          <g>
            <g>
              <path className={cx('st7')} d="M20.5,66.6" />
            </g>
          </g>
          <g className="head">
            <path
              className={cx('st5')}
              d="M24.5,47.5c-3.3,0-5.9-2.7-5.9-5.9s2.7-5.9,5.9-5.9h3.4v11.8L24.5,47.5z"
            />
            <path
              className={cx('st5')}
              d="M123.2,35.6c3.3,0,5.9,2.7,5.9,5.9s-2.7,5.9-5.9,5.9h-3.4V35.7L123.2,35.6z"
            />
            <path
              className={cx('st3')}
              d="M102.2,59.1H43.8c-10,0-18.2-8.1-18.2-18.2v0c0-10,8.1-18.2,18.2-18.2h58.5c10,0,18.2,8.1,18.2,18.2v0
            C120.4,51,112.3,59.1,102.2,59.1z"
            />
            <path
              className={cx('st5')}
              d="M74,47L74,47c-1.9,0-4.2-1.5-4.2-3.4c0-3.8,1.9-11,4.2-11c2.3,0,4.2,7.6,4.2,11C78.2,45.4,75.9,47,74,47z"
            />
            <g transform="translate (0 3)">
              <path
                className={cx('st8')}
                d="M80.6,51.4c-1.7,0.5-12.2,0.7-13.4,0"
              />
            </g>
            <g>
              <rect
                x="103.4"
                y="20.8"
                className={cx('st9')}
                width="5"
                height="40.7"
              />
              <rect
                x="39.2"
                y="20.8"
                className={cx('st9')}
                width="5"
                height="40.7"
              />
            </g>
            <line
              className={cx('st1')}
              x1="72.9"
              y1="9.3"
              x2="72.9"
              y2="23.2"
            />
            <circle className={cx('st9')} cx="72.9" cy="3.1" r="3.1" />
            <g className="mouth" transform="translate (62.5 49)">
              <g className={cx('mouth-animation')}>
                <path
                  className={cx('st0d')}
                  d="M3.3,5.5C2.1,5.3,1.3,3,1.3,3L1.1,2.5C0.8,3,0.4,3.5,0.2,4.2l0,0C0.1,4.4,0,4.7,0,4.9l0,0c0,0,0,0,0,0
	c0,0,0,0,0,0c-0.1,1.3,0.8,2.6,1.6,3.5C1.7,7.6,2.1,5.8,3.3,5.5z"
                  fill="#1a272f"
                />
                <path
                  className={cx('st0d')}
                  d="M22.3,5L22.3,5C22.3,5,22.3,5,22.3,5c0-0.3-0.1-0.6-0.2-0.9c-0.2-0.5-0.4-1-0.7-1.4L21.4,3l0,0
	c0,0-0.8,2.5-2.2,2.5c0,0,0,0-0.1,0c1.2,0.4,1.6,2.1,1.7,2.7C21.6,7.5,22.3,6.3,22.3,5C22.3,5.1,22.3,5.1,22.3,5z"
                  fill="#1a272f"
                />
                <path
                  className={cx('st1d')}
                  d="M19.1,5.6C19.1,5.5,19.2,5.5,19.1,5.6C20.5,5.5,21.4,3,21.4,3l0,0l0.1-0.2c-0.3-0.4-0.5-0.8-0.7-1
	c-0.5-0.7-1.3-1.4-2.5-1.6C18.1,0,17.8,0,17.6,0h-2.4h-8H4.7C4.5,0,4.3,0,4.1,0.1C3.2,0.3,2.5,0.7,2,1.2c0,0-0.1,0.2-0.4,0.4
	c0,0,0,0,0,0C1.5,1.9,1.3,2.1,1.1,2.5L1.3,3c0,0,0.8,2.3,2,2.5C2.1,5.8,1.7,7.6,1.6,8.4C1.8,8.6,2,8.8,2.1,8.9
	c0.2,0.2,0.4,0.3,0.6,0.5c0,0,0.1,0,0.1,0l0,0C3.2,9.7,3.7,10,4.3,10c0.2,0,0.3,0.1,0.5,0.1h2.4h8h2.4c0.2,0,0.3,0,0.5-0.1
	c0.6-0.1,1.1-0.3,1.5-0.6l0,0c0,0,0,0,0,0c0.2-0.2,0.5-0.4,0.7-0.6c0.2-0.2,0.4-0.4,0.6-0.6C20.7,7.6,20.3,5.9,19.1,5.6z"
                  fill="#fff"
                />
              </g>
            </g>
          </g>
          <g className={cx('screen')}>
            <rect
              x="50.7"
              y="110"
              className={cx('st2')}
              width="47"
              height="28.8"
            />
            <g className={cx('loader-background')}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                x="55"
                y="104"
                width="40.6"
                height="40.6"
                overflow="visible"
              >
                <radialGradient
                  id="a"
                  cx="20.297"
                  cy="20.297"
                  r="15.297"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor="#de4c26" stopOpacity=".5" />
                  <stop offset="1" stopColor="#de4c26" stopOpacity=".1" />
                </radialGradient>
                <circle cx="20.3" cy="20.3" r="15.3" fill="url(#a)" />
              </svg>
            </g>
            <svg
              x="65"
              y="114"
              width="22"
              height="20"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g className={cx('circular-loader')}>
                <circle className={cx('loader-path')} cx="50" cy="50" r="40" />
              </g>
            </svg>
          </g>
          <g className={cx('robot_arm_r')}>
            <path
              className={cx('st0')}
              d="M37.2,128.6h-6.5c-16.2,0-29.4-13.2-29.4-29.4V89h10.4v10.2c0,10.5,8.5,19,19,19h6.5V128.6z"
            />
            <g>
              <line
                className={cx('st1')}
                x1="2"
                y1="105.5"
                x2="11.9"
                y2="103.3"
              />
              <line
                className={cx('st1')}
                x1="1.3"
                y1="96.8"
                x2="11.5"
                y2="96.8"
              />
              <line
                className={cx('st1')}
                x1="29.1"
                y1="128.5"
                x2="29.7"
                y2="118.4"
              />
              <line
                className={cx('st1')}
                x1="5.5"
                y1="114.4"
                x2="14.2"
                y2="109.1"
              />
              <line
                className={cx('st1')}
                x1="11.7"
                y1="121.7"
                x2="18.3"
                y2="113.9"
              />
              <line
                className={cx('st1')}
                x1="20"
                y1="126.3"
                x2="23.8"
                y2="116.8"
              />
            </g>
          </g>
          <g transform="translate (95 38)">
            <g className={cx('robot_pupil_l-animation')}>
              <circle className={cx('st2')} cx="0" cy="0" r="5.1" />
            </g>
          </g>
          <g transform="translate (55.2 38.6)">
            <g className={cx('robot_pupil_r-animation')}>
              <circle className={cx('st2')} r="5.1" />
            </g>
          </g>
        </g>
      </svg>
      <Text className={cx('spinner__text')}>{visibleText}</Text>
    </div>
  )
}

export default RobotSpinner
